/**
 * Centralized formatters. Use these everywhere — never instantiate `Intl.*`
 * inline.
 *
 * `price` = property prices (integers, no decimals): "289 000 €".
 * `priceWithCents` = subscription prices (always 2 decimals): "7,59 €".
 */
export const price = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
})

/** Compact price for map pills: 289000 → "289 k€", 1250000 → "1,3 M€" (one decimal). */
export function priceCompact(value: number): string {
  if (value >= 1_000_000) {
    const m = Math.round(value / 100_000) / 10
    return `${m.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} M€`
  }
  return `${Math.round(value / 1000).toLocaleString('fr-FR')} k€`
}

/** Property price, or the "Non estimé" sentinel when the back returned no estimate (null). */
export function priceOrUnknown(value: number | null): string {
  return value == null ? 'Non estimé' : price.format(value)
}

export const priceWithCents = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

export const pricePerSquareMeter = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
})

export const date = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: 'short',
})

export const dateLong = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
})

export const dateTimeLong = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
})

export const integer = new Intl.NumberFormat('fr-FR')

/** Compte entier, suffixé « + » quand le back ne renvoie qu'un minimum : 5000 → "5 000+". */
export function countAtLeast(value: number, atLeast: boolean): string {
  return `${integer.format(value)}${atLeast ? '+' : ''}`
}

/** Placeholder for a missing datapoint. Never render a fabricated `0`. */
export const NO_DATA = '—'

const surfaceNumber = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 })

/** Living surface: 52.3 → "52 m²" ; null/0/NaN → "—". */
export function surfaceM2(value: number | null | undefined): string {
  return value == null || !Number.isFinite(value) || value <= 0
    ? NO_DATA
    : `${surfaceNumber.format(value)} m²`
}

const percentFormat = new Intl.NumberFormat('fr-FR', { style: 'percent', maximumFractionDigits: 0 })

/** Ratio 0–1 as a French percentage: 0.42 → "42 %" ; null/NaN → "—". */
export function percent(ratio: number | null | undefined): string {
  return ratio == null || !Number.isFinite(ratio) ? NO_DATA : percentFormat.format(ratio)
}

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/

/**
 * `Date` d'une valeur de l'API. Une date seule (`"2026-07-12"`, ex. la date
 * d'établissement du DPE, jour de Paris) est lue comme minuit LOCAL : le
 * `new Date("2026-07-12")` natif vaut minuit UTC, soit le 11 au soir à l'ouest
 * de Greenwich (Antilles, Guyane). Les horodatages complets passent tels quels.
 */
export function parseApiDate(value: string | number | Date): Date {
  const m = typeof value === 'string' ? DATE_ONLY.exec(value) : null
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(value)
}

/** Safe long date: returns `null` when the value is missing or unparseable. */
export function formatDateLong(value: string | number | Date | null | undefined): string | null {
  if (value == null) return null
  const d = parseApiDate(value)
  return Number.isNaN(d.getTime()) ? null : dateLong.format(d)
}

/** Safe long date+time: returns `null` when the value is missing or unparseable. */
export function formatDateTimeLong(value: string | number | Date | null | undefined): string | null {
  if (value == null) return null
  const d = parseApiDate(value)
  return Number.isNaN(d.getTime()) ? null : dateTimeLong.format(d)
}

/**
 * Compact relative "reçu" label, matching the POC (2 h, hier, 3 j…) ; "—" when unknown.
 * Une date seule se compte en jours calendaires (« aujourd'hui », « hier »…) : des
 * heures écoulées depuis minuit n'auraient pas de sens.
 */
export function relTime(iso: string | null | undefined): string {
  if (!iso) return NO_DATA
  const t = parseApiDate(iso).getTime()
  if (Number.isNaN(t)) return NO_DATA
  let d: number
  if (DATE_ONLY.test(iso)) {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    // Arrondi : absorbe l'heure gagnée/perdue d'un changement d'heure.
    d = Math.round((today.getTime() - t) / 86_400_000)
    if (d <= 0) return "aujourd'hui"
  } else {
    const h = (Date.now() - t) / 3_600_000
    if (h < 1) return "à l'instant"
    if (h < 24) return `${Math.round(h)} h`
    d = Math.round(h / 24)
  }
  if (d === 1) return 'hier'
  if (d < 7) return `${d} j`
  const w = Math.round(d / 7)
  if (w < 5) return `${w} sem.`
  return `${Math.round(d / 30)} mois`
}

/** Capitalise the first letter (e.g. backend lowercase enums like `appartement`). */
export function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1)
}
